import { useMutation, useQuery, useQueryClient } from 'react-query';
import { addLog,assignBDPOC, createLeadGen, createLeadGenSource, getAllLeadGensSource, getAllLeadGensUser, getLeadGenData, reAssignLeadGenPOC, updateLeadgen, uploadLeadGen } from 'src/apis/leadGen';
import { getMyBDPocs } from 'src/apis/user';
import { AssignCompanyData, CreateLeadGenOptions, CreateLeadGenSourceOptions, FilterLeadGenInput, GetMyBDPocsAPIResponse, LeadData, LeadGenAPIResponse, LeadGensource, LeadGenSourceAPIResponse, UploadLeadData, UploadLeadDataOptions } from 'types';

export const useUploadLeadGen = (
	onComplete?: (data: LeadGenAPIResponse) => void,
	handleError?: (error: any) => void,
	options?: UploadLeadDataOptions,
) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (payload: { data: LeadData[] }) => await uploadLeadGen(payload),
		onSuccess(data) {
			queryClient.invalidateQueries('leadGens');
			onComplete && onComplete(data);
			options?.onSuccess && options.onSuccess(data);
		},
		onError(error) {
			handleError && handleError(error);
			options?.onError && options.onError(error);
		},
	});
};


export const useGetLeadGenData = (
	args: FilterLeadGenInput,
	onComplete?: (data: LeadGenAPIResponse) => void,
	onSettled?: () => void,
) => {
	return useQuery({
		queryKey: ['getLeadGenData', ...Object.values(args)],
		queryFn: async () => (await getLeadGenData(args)),
		staleTime: 0,
		refetchOnMount: true,
		onSuccess(data) {
			onComplete && onComplete(data);
		},
		onSettled: () => {
			onSettled && onSettled();
		},
	});
};


export const useCreateLeadGen = (
	onComplete?: (data: LeadGenAPIResponse) => void,
	handleError?: (error: any) => void,
	options?: CreateLeadGenOptions,
) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (payload: LeadData) => await createLeadGen(payload),
		onSuccess(data) {
			queryClient.invalidateQueries('leadGens');
			onComplete && onComplete(data);
			options?.onSuccess && options.onSuccess(data);
		},
		onError(error) {
			handleError && handleError(error);
			options?.onError && options.onError(error);
		},
	});
};

export const useCreateLeadGenSource = (
	onComplete?: (data: LeadGenSourceAPIResponse) => void,
	handleError?: (error: any) => void,
	options?: CreateLeadGenSourceOptions,
) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (payload: LeadGensource) => await createLeadGenSource(payload),
		onSuccess(data) {
			queryClient.invalidateQueries('leadGens');
			onComplete && onComplete(data);
			options?.onSuccess && options.onSuccess(data);
		},
		onError(error) {
			handleError && handleError(error);
			options?.onError && options.onError(error);
		},
	});
};


export const useAssignBDPOC = (
	onComplete?: (data: LeadGenAPIResponse) => void,
	handleError?: (error: any) => void,
	options?: CreateLeadGenOptions,
) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (payload: AssignCompanyData) => await assignBDPOC(payload),
		onSuccess(data) {
			queryClient.invalidateQueries('leadGens');
			onComplete && onComplete(data);
			options?.onSuccess && options.onSuccess(data);
		},
		onError(error) {
			handleError && handleError(error);
			options?.onError && options.onError(error);
		},
	});
};

export const useAddLog = (
	onComplete?: (data: LeadGenAPIResponse) => void,
	handleError?: (error: any) => void,
) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (payload: { ids: any; data: any }) => await addLog(payload),
		onSuccess(data) {
			queryClient.invalidateQueries('addLog');
			onComplete && onComplete(data);
		},
		onError(error) {
			handleError && handleError(error);
		},
	});
}

export const useReassignLeadGenPOC = (
    onComplete?: (data: LeadGenAPIResponse) => void,
    handleError?: (error: any) => void,
) => {
    return useMutation({
        mutationFn: ({ leadId, newPocId }: { leadId: any; newPocId: string }) =>
            reAssignLeadGenPOC({ id: leadId, data: { leadGen: leadId, newPocId } }),
        onSuccess: (data) => {
            onComplete?.(data);
        },
        onError: (error) => {
            handleError?.(error);
        },
    });
};

export const useGetAllLeadGenUsers = (
	onComplete?: (data: LeadGenAPIResponse) => void,
	onSettled?: () => void,
) => {
	return useQuery({
		queryKey: ['getAllLeadGensUser'],
		queryFn: async () => (await getAllLeadGensUser()) as LeadGenAPIResponse,
		staleTime: 0,
		refetchOnMount: true,
		onSuccess(data) {
			onComplete && onComplete(data);
		},
		onSettled: () => {
			onSettled && onSettled();
		},
	});
}

export const useGetAllLeadGenSource = (
	onComplete?: (data: LeadGenSourceAPIResponse) => void,
	onSettled?: () => void,
) => {
	return useQuery({
		queryKey: ['getAllLeadGenSource'],
		queryFn: async () => (await getAllLeadGensSource()) as LeadGenSourceAPIResponse,
		staleTime: 0,
		refetchOnMount: true,
		onSuccess(data) {
			onComplete && onComplete(data);
		},
		onSettled: () => {
			onSettled && onSettled();
		},
	});
}

export const useUpdateLeadGen = (
	onComplete?: (data: LeadGenAPIResponse) => void,
	handleError?: (error: any) => void,
	options?: CreateLeadGenOptions,
) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async ({ id, action }: { id: string; action?: string }) => await updateLeadgen({ id, action: action || '' }),
		onSuccess(data) {
			queryClient.invalidateQueries('leadGens');
			onComplete && onComplete(data);
			options?.onSuccess && options.onSuccess(data);
		},
		onError(error) {
			handleError && handleError(error);
			options?.onError && options.onError(error);
		},
	});
};

// export const useGetMyBDPocsQuery = (onComplete?: (data: GetMyBDPocsAPIResponse) => void) => {
// 	return useQuery({
// 		queryKey: ['getMyBDPocs'],
// 		queryFn: async () => (await getMyBDPocs()) as GetMyBDPocsAPIResponse,
// 		staleTime: 0,
// 		refetchOnMount: true,
// 		onSettled(data) {
// 			if (data) onComplete && onComplete(data);
// 		},
// 	});
// };